/**
 * Central error handler — maps API errors to friendly French messages.
 * Usage:  import { getErrorMessage } from '../utils/errorMessages';
 *         toast({ description: getErrorMessage(error), variant: 'destructive' });
 */

const STATUS_MESSAGES = {
  400: 'Les informations envoyées sont incorrectes.',
  401: 'Votre session a expiré. Veuillez vous reconnecter.',
  403: "Vous n'avez pas accès à cette page.",
  404: "Cette page n'existe pas.",
  408: 'Le serveur met trop de temps à répondre. Veuillez réessayer.',
  409: 'Cette action est en conflit avec une autre opération.',
  422: 'Les informations envoyées sont invalides.',
  429: 'Trop de requêtes. Veuillez patienter quelques instants.',
  500: 'Une erreur est survenue. Veuillez réessayer.',
  502: 'Le serveur est temporairement indisponible.',
  503: 'Le service est en maintenance. Veuillez réessayer plus tard.',
};

const CONTEXT_MESSAGES = {
  login: 'Email ou mot de passe incorrect.',
  register: "Erreur lors de l'inscription. Vérifiez vos informations.",
  'forgot-password': "Impossible d'envoyer l'email de réinitialisation.",
  'reset-password': 'Lien invalide ou expiré.',
  profile: 'Impossible de mettre à jour le profil.',
  scan: 'Erreur lors du scan.',
};

export function getErrorMessage(error, context) {
  // Network / no response
  if (!error.response) {
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return 'Une erreur de connexion est survenue. Vérifiez votre connexion internet.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'Le serveur met trop de temps à répondre. Veuillez réessayer.';
    }
    return 'Une erreur inattendue est survenue. Veuillez réessayer.';
  }

  const status = error.response.status;
  const serverMessage = error.response?.data?.detail;

  // For login specifically, always show the same message (prevents enumeration)
  if (context === 'login' && (status === 401 || status === 400)) {
    return CONTEXT_MESSAGES.login;
  }

  // Use context-specific message for 400-level errors
  if (context && CONTEXT_MESSAGES[context] && status >= 400 && status < 500) {
    return CONTEXT_MESSAGES[context];
  }

  // If server sent a readable French message, use it
  if (serverMessage && typeof serverMessage === 'string' && !serverMessage.includes('Error') && !serverMessage.includes('error')) {
    return serverMessage;
  }

  // Fall back to status code mapping
  return STATUS_MESSAGES[status] || 'Une erreur est survenue. Veuillez réessayer.';
}

/**
 * Show a toast with the appropriate error message.
 * Requires the toast function from useToast().
 */
export function showErrorToast(toast, error, context) {
  toast({
    title: 'Erreur',
    description: getErrorMessage(error, context),
    variant: 'destructive',
  });
}

/**
 * Show a success toast.
 */
export function showSuccessToast(toast, message) {
  toast({
    title: 'Succès',
    description: message,
  });
}
